/**
 * Daily Run bookkeeping (spec 6.3): one scored attempt per day per device —
 * the FIRST full attempt (start → death or finish) writes the result; after
 * that, unlimited clearly-labeled practice. Streak = consecutive days with a
 * scored attempt. Share string is Wordle-style plain text.
 */
import type { SaveStore } from "./saveStore";
import { formatTimeMs } from "../ui/hud";

export interface DailyResult {
  date: string; // YYYY-MM-DD (UTC)
  progressPct: number;
  timeMs?: number;
  seedHash: string;
}

export class DailyManager {
  constructor(private readonly save: SaveStore) {}

  hasScored(key: string): boolean {
    return this.save.daily.lastScoredDate === key;
  }

  get streak(): number {
    return this.save.daily.streak;
  }

  bestEver(): DailyResult | null {
    let best: DailyResult | null = null;
    for (const r of this.save.daily.history as DailyResult[]) {
      if (
        !best ||
        r.progressPct > best.progressPct ||
        (r.progressPct === best.progressPct &&
          r.timeMs !== undefined &&
          (best.timeMs === undefined || r.timeMs < best.timeMs))
      ) {
        best = r;
      }
    }
    return best;
  }

  todayResult(key: string): DailyResult | null {
    return (
      (this.save.daily.history as DailyResult[]).find((r) => r.date === key) ?? null
    );
  }

  /**
   * Record the scored attempt. No-op (returns false) if today already scored —
   * the one-attempt lock.
   */
  recordScored(result: DailyResult): boolean {
    if (this.hasScored(result.date)) return false;
    const daily = this.save.daily;
    const prev = daily.lastScoredDate;
    daily.streak = prev === yesterdayOf(result.date) ? daily.streak + 1 : 1;
    daily.lastScoredDate = result.date;
    daily.history.push(result);
    if (daily.history.length > 60) daily.history.splice(0, daily.history.length - 60);
    this.save.persist();
    return true;
  }
}

export function dailyResultOf(
  date: string,
  progressPct: number,
  timeMs: number | undefined,
  seedHash: string,
): DailyResult {
  const r: DailyResult = { date, progressPct, seedHash };
  if (timeMs !== undefined) r.timeMs = timeMs;
  return r;
}

/** "YYYY-MM-DD" of the day before (UTC math, no locale). */
export function yesterdayOf(key: string): string {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** `SKYPULSE Daily 2026-08-07 — 94% — 1:12.480 🔥` (spec 6.3). */
export function shareString(result: DailyResult, streak: number): string {
  const pct = `${Math.floor(result.progressPct)}%`;
  const time = result.timeMs !== undefined ? ` — ${formatTimeMs(result.timeMs)}` : "";
  const flame = streak > 1 ? ` 🔥${streak}` : result.timeMs !== undefined ? " 🔥" : "";
  return `SKYPULSE Daily ${result.date} — ${pct}${time}${flame}`;
}
